/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2020 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2020 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import React from 'react';
import { TimeSliderButtons } from '@opengeoweb/time-slider';
import { AutoUpdateButtonConnect } from './TimeSliderButtonsConnect/AutoUpdateButtonConnect';
import { BackwardForwardStepButtonConnect } from './TimeSliderButtonsConnect/BackwardForwardStepButtonConnect';
import { NowButtonConnect } from './TimeSliderButtonsConnect/NowButtonConnect';
import { OptionsMenuButtonConnect } from './TimeSliderButtonsConnect/OptionsMenuButtonConnect';
import { PlayButtonConnect } from './TimeSliderButtonsConnect/PlayButtonConnect';

interface TimeSliderButtonsConnectProps {
  sourceId: string;
  mapId: string;
  mapIsActive?: boolean;
}

/**
 * TimeSliderButtons component with components connected to the store displaying the time slider buttons
 *
 * Expects the following props:
 * @param {string} mapId mapId: string - Id of the map
 * @param {string} sourceId mapId: string - Id of the source
 * @example
 * ``` <TimeSliderButtonsConnect mapId={mapId} sourceId={sourceId} />```
 */

const TimeSliderButtonsConnectComponent: React.FC<
  TimeSliderButtonsConnectProps
> = ({ sourceId, mapId, mapIsActive }: TimeSliderButtonsConnectProps) => (
  <TimeSliderButtons
    autoUpdateBtn={<AutoUpdateButtonConnect mapId={mapId} />}
    backwardBtn={
      <BackwardForwardStepButtonConnect isForwardStep={false} mapId={mapId} />
    }
    forwardBtn={
      <BackwardForwardStepButtonConnect isForwardStep={true} mapId={mapId} />
    }
    nowBtn={<NowButtonConnect mapId={mapId} sourceId={sourceId} />}
    optionsMenuBtn={
      <OptionsMenuButtonConnect
        enableKeyboardShortcut={mapIsActive}
        mapId={mapId}
      />
    }
    playBtn={<PlayButtonConnect mapId={mapId} />}
  />
);

export const TimeSliderButtonsConnect = React.memo(
  TimeSliderButtonsConnectComponent,
);
